import { defineMessages } from "react-intl";

/**
 * i18n keys referenced by the 8 task-creation config fixtures
 * (`../fixtures/*.json`): each `hints_intro`, `hints` entry, `conditional_hints[].hints`
 * entry, `option_hints` value and `placeholder` string in those files is an id
 * declared here. The config carries only keys — see PLAN.md decision 10.
 *
 * Placed under `config/` (rather than `configMessages.ts` next to it) so this
 * file is still named `messages.ts` and gets picked up by the `extract-intl`
 * script, whose glob only matches files named exactly `messages.ts` at any
 * depth under `app/frontend` (see the `extract-intl` entry in
 * `package.json`) — `App/config/messages.ts` is the existing precedent for
 * this exact pattern in the codebase.
 *
 * DEFAULT MESSAGES ARE PLACEHOLDERS. The real English copy lives in the
 * Google Doc linked from PLAN.md
 * (https://docs.google.com/document/d/13fxsTUUztTPbpkeEsY2_YvI86po65KPeOdfEe5dJvE8),
 * which nobody running this file has access to. Every `defaultMessage` below
 * is mechanically derived from the key's own last segment(s) so the app
 * renders *something* readable and the translation round trip is not blocked —
 * replace them with the copy brief's real strings when it lands. Do not
 * treat these as final or user-facing copy.
 *
 * Note for product: the Google Doc's painter `extraDetails` copy is known to
 * disagree with this config's keys (PLAN.md, M3 milestone detail). Per
 * decision 1 the config wins; the doc needs reconciling, not this file.
 *
 * `taskCreation.category.*` (RT-4321, category data layer) is a second,
 * unrelated block bolted onto this same file: display names for the
 * Bricolage theme and its 8 s_categories, keyed by the `keyName` carried by
 * `fixtures/bricolageCategories.json` / `services/categories.ts` (which
 * deliberately carry no `name` field — the real `CategoryResource` doesn't
 * serialize one either; names resolve client-side, same idea as the mobile
 * app's `i18n.t(item.keyName)`).
 *
 * `configMessages.test.ts`'s "no unreferenced key" coverage check is scoped
 * to keys the 8 fixtures can reference (`placeholder`, `hints`, etc.) — the
 * category keys are exempted there since fixtures never reference display
 * names.
 *
 * English defaults are plural, lowercase mid-sentence — they get
 * interpolated into copy like "Get offers from top {category} near you!".
 * The `diy` entry's copy ("DIY pros") is an invented placeholder needing
 * product sign-off — see the task report.
 */

const configMessages = defineMessages({
  /* Shared across all 8 categories. */
  "taskCreation.audience.option.everyone.hint": {
    id: "taskCreation.audience.option.everyone.hint",
    defaultMessage:
      "Individuals and certified professionals. The request reaches every provider at Ring Twice",
  },
  "taskCreation.audience.option.certifiedOnly.hint": {
    id: "taskCreation.audience.option.certifiedOnly.hint",
    defaultMessage:
      "For work requiring a VAT invoice: claims, subsidized work, professional requests.",
  },
  /* Asked by electrician and plumber alike — one key, not one per trade. */
  "taskCreation.extraDetails.hint.newInstallationOrFix": {
    id: "taskCreation.extraDetails.hint.newInstallationOrFix",
    defaultMessage: "New installation or fix",
  },
  "taskCreation.extraDetails.hint.workAndMaterials": {
    id: "taskCreation.extraDetails.hint.workAndMaterials",
    defaultMessage: "Work and materials",
  },
  "taskCreation.photos.hint.workToBeDone": {
    id: "taskCreation.photos.hint.workToBeDone",
    defaultMessage: "Work to be done",
  },
  /* "Other" reads the same in every category: declared once per question
     instead of once per trade, which the option getter falls back to. */
  "taskCreation.applianceType.option.other": {
    id: "taskCreation.applianceType.option.other",
    defaultMessage: "Other",
  },
  "taskCreation.surfaceType.option.other": {
    id: "taskCreation.surfaceType.option.other",
    defaultMessage: "Other",
  },
  "taskCreation.taskType.option.other": {
    id: "taskCreation.taskType.option.other",
    defaultMessage: "Other",
  },
  "taskCreation.extraDetails.hintsIntro": {
    id: "taskCreation.extraDetails.hintsIntro",
    defaultMessage: "Please include these often missed details:",
  },
  /**
   * One worked example per category — a plumber should not be shown a painting
   * example. Wording comes from the translation sheet's per-category rows.
   */
  "taskCreation.painter.extraDetails.placeholder": {
    id: "taskCreation.painter.extraDetails.placeholder",
    defaultMessage:
      "Ex. A few holes and some damp on the wall. I'll provide the paint myself \u2026",
  },
  "taskCreation.electrician.extraDetails.placeholder": {
    id: "taskCreation.electrician.extraDetails.placeholder",
    defaultMessage:
      "Ex. No power in the kitchen, ongoing outage. I have replacement sockets \u2026",
  },
  "taskCreation.carpenter.extraDetails.placeholder": {
    id: "taskCreation.carpenter.extraDetails.placeholder",
    defaultMessage:
      "Ex. About 2m wide, modern style, light wood with a matte finish \u2026",
  },
  "taskCreation.plumber.extraDetails.placeholder": {
    id: "taskCreation.plumber.extraDetails.placeholder",
    defaultMessage:
      "Ex. Replace a damaged pipe under the sink, I already have what's needed \u2026",
  },
  "taskCreation.applianceRepairer.extraDetails.placeholder": {
    id: "taskCreation.applianceRepairer.extraDetails.placeholder",
    defaultMessage:
      "Ex. My electric oven isn't heating at all anymore, even after several tries \u2026",
  },
  "taskCreation.handyman.extraDetails.placeholder": {
    id: "taskCreation.handyman.extraDetails.placeholder",
    defaultMessage:
      "Ex. Mount a shelf and fix a door handle. I have basic tools \u2026",
  },
  "taskCreation.lockRepairer.extraDetails.placeholder": {
    id: "taskCreation.lockRepairer.extraDetails.placeholder",
    defaultMessage:
      "Ex. Replace the front door lock, it's a new installation \u2026",
  },
  "taskCreation.bicycleRepairer.extraDetails.placeholder": {
    id: "taskCreation.bicycleRepairer.extraDetails.placeholder",
    defaultMessage:
      "Ex. Bike model MX 455, the derailleur isn't shifting gears properly anymore \u2026",
  },
  "taskCreation.photos.hintsIntro": {
    id: "taskCreation.photos.hintsIntro",
    defaultMessage: "Photos hints intro",
  },

  /* The extra-details screen's own copy — taken from the Figma design (mobile
     1503-13343 / desktop 1503-16125). Category-independent: the heading and the
     subtitle say nothing about the trade, so they are declared once without a
     category segment and `getScreenTitleMessage` falls back to them. The
     trade-specific part of this screen is the hint list, which the config
     already keys per category.

     Note for product: Figma reads "any extra information your want to let your
     provider know about" — "your" is a typo there and is corrected here rather
     than sent to translation. */
  "taskCreation.extraDetails.screenTitle": {
    id: "taskCreation.extraDetails.screenTitle",
    defaultMessage: "Details about the job",
  },
  "taskCreation.extraDetails.screenSubtitle": {
    id: "taskCreation.extraDetails.screenSubtitle",
    defaultMessage:
      "This is a space to mention any extra information you want to let your provider know about.",
  },

  /* The photos screen's own copy — taken from the Figma design (mobile
     1503-13220 / desktop 1503-16314), not from the config, which carries no
     screen-level keys. Shared across the 8 categories: the count is the
     screen's own `constraints.max_files`, interpolated by `DynamicScreen`. */
  "taskCreation.photos.screenTitle": {
    id: "taskCreation.photos.screenTitle",
    defaultMessage: "Add your photos (up to {maxFiles})",
  },
  "taskCreation.photos.screenSubtitle": {
    id: "taskCreation.photos.screenSubtitle",
    defaultMessage:
      "Add photos to help providers understand the issue and bring the right tools. Clearer details mean better offers for you!",
  },

  /* The time screen's own copy — Figma (mobile 1503-13425 / desktop
     1503-16637). Shared across the 8 categories: the line names no trade.
     The screen carries no subtitle: `getScreenSubtitleMessage` finding no key
     is what removes it, since `DynamicScreen` renders that block only when the
     lookup resolves. */
  "taskCreation.time.screenTitle": {
    id: "taskCreation.time.screenTitle",
    defaultMessage: "Availability",
  },

  /* The address screen's own copy — Figma (mobile 2063-6681 / desktop
     1585-16861). Shared for the same reason; the example address itself is
     frontend-owned copy and lives in `../messages.ts`, since no config key
     points at it. */
  "taskCreation.address.screenTitle": {
    id: "taskCreation.address.screenTitle",
    defaultMessage: "Address for the service",
  },
  "taskCreation.address.screenSubtitle": {
    id: "taskCreation.address.screenSubtitle",
    defaultMessage: "Please indicate your full address like this:",
  },

  /* The visibility screen's own copy — Figma (mobile 1503-13716 / desktop
     1585-17007). Shared for the same reason: neither line names a trade.

     The screen declares no title of its own: it holds one question, so
     `DynamicScreen` falls through to `taskCreation.audience.title`, which is
     the same sentence. Declaring both meant two keys and two translations for
     one heading. */
  "taskCreation.visibility.screenSubtitle": {
    id: "taskCreation.visibility.screenSubtitle",
    defaultMessage:
      "The request can be sent to a specific portion of our vast roster of service providers.",
  },

  /* Question titles and option labels shared by all 8 categories — same idea
     as the shared screen titles, one segment shorter (no category). The
     `desired_timing` question is byte-identical in all 8 fixtures, so
     declaring it per category would ship 40 duplicate strings to translate for
     the 5 distinct labels it actually has. `getQuestionTitleMessage` /
     `getQuestionOptionMessage` fall back to these when no category-scoped key
     exists.

     Shared option labels stop at the option segment, one segment short of the
     config's own `option_hints` ids (`...option.everyone.hint`, declared at
     the top of this file) — same question, different key, no collision. */
  /* `audience`. The title is never rendered — the visibility screen declares
     its own heading above and holds this one question — but it is declared for
     the same reason `desiredTiming.title` is: every select question owes one,
     and a config that moved this question next to another would need it. */
  "taskCreation.audience.title": {
    id: "taskCreation.audience.title",
    defaultMessage: "Who should see the request?",
  },
  "taskCreation.audience.option.everyone": {
    id: "taskCreation.audience.option.everyone",
    defaultMessage: "Everybody",
  },
  "taskCreation.audience.option.certifiedOnly": {
    id: "taskCreation.audience.option.certifiedOnly",
    defaultMessage: "Only certified professionals",
  },
  "taskCreation.desiredTiming.title": {
    id: "taskCreation.desiredTiming.title",
    defaultMessage: "When would you like the service?",
  },
  "taskCreation.desiredTiming.option.asap": {
    id: "taskCreation.desiredTiming.option.asap",
    defaultMessage: "As soon as possible",
  },
  "taskCreation.desiredTiming.option.thisWeek": {
    id: "taskCreation.desiredTiming.option.thisWeek",
    defaultMessage: "Within this week",
  },
  "taskCreation.desiredTiming.option.flexible": {
    id: "taskCreation.desiredTiming.option.flexible",
    defaultMessage: "I'm flexible (not urgent)",
  },
  /* The space before the colon is a NO-BREAK SPACE (U+00A0), not a plain one:
     French typography wants a space there, and a plain one lets the label wrap
     with the colon stranded alone on the second line once the date pill
     squeezes the row. Keep it non-breaking in every translation. */
  "taskCreation.desiredTiming.option.beforeDate": {
    id: "taskCreation.desiredTiming.option.beforeDate",
    defaultMessage: "Before this date :",
  },

  /* Extra-details hints, conditional on the screen's own answers. */
  // Appliance repairer
  "taskCreation.applianceRepairer.extraDetails.hint.affectedFunction": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.affectedFunction",
    defaultMessage: "Affected function",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.alarmSounding": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.alarmSounding",
    defaultMessage: "Alarm sounding",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.coolingZones": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.coolingZones",
    defaultMessage: "Cooling zones",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.describeAppliance": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.describeAppliance",
    defaultMessage: "Describe appliance",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.doorCloses": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.doorCloses",
    defaultMessage: "Door closes",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.errorCode": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.errorCode",
    defaultMessage: "Error code",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.frostOrNoise": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.frostOrNoise",
    defaultMessage: "Frost or noise",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.hoodIssueType": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.hoodIssueType",
    defaultMessage: "Hood issue type",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.ovenAffectedFunction": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.ovenAffectedFunction",
    defaultMessage: "Oven affected function",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.powerSupplyType": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.powerSupplyType",
    defaultMessage: "Power supply type",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.waterInTub": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.waterInTub",
    defaultMessage: "Water in tub",
  },
  "taskCreation.applianceRepairer.extraDetails.hint.waterLeftInDrum": {
    id: "taskCreation.applianceRepairer.extraDetails.hint.waterLeftInDrum",
    defaultMessage: "Water left in drum",
  },
  // Bicycle repairer
  "taskCreation.bicycleRepairer.extraDetails.hint.affectedPart": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.affectedPart",
    defaultMessage: "Affected part",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.bikeAndGroupset": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.bikeAndGroupset",
    defaultMessage: "Bike and groupset",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.bikeAndMotorSystem": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.bikeAndMotorSystem",
    defaultMessage: "Bike and motor system",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.bikeBrandModel": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.bikeBrandModel",
    defaultMessage: "Bike brand model",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.brakeType": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.brakeType",
    defaultMessage: "Brake type",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.ebikeFault": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.ebikeFault",
    defaultMessage: "Ebike fault",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.gearsSymptom": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.gearsSymptom",
    defaultMessage: "Gears symptom",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.lastService": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.lastService",
    defaultMessage: "Last service",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.wheelAndTireSize": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.wheelAndTireSize",
    defaultMessage: "Wheel and tire size",
  },
  "taskCreation.bicycleRepairer.extraDetails.hint.wheelTrueOrSpoke": {
    id: "taskCreation.bicycleRepairer.extraDetails.hint.wheelTrueOrSpoke",
    defaultMessage: "Wheel true or spoke",
  },
  // Carpenter
  "taskCreation.carpenter.extraDetails.hint.doorsWindowsSpec": {
    id: "taskCreation.carpenter.extraDetails.hint.doorsWindowsSpec",
    defaultMessage: "Doors windows spec",
  },
  "taskCreation.carpenter.extraDetails.hint.flooringSpec": {
    id: "taskCreation.carpenter.extraDetails.hint.flooringSpec",
    defaultMessage: "Flooring spec",
  },
  "taskCreation.carpenter.extraDetails.hint.furnitureSpec": {
    id: "taskCreation.carpenter.extraDetails.hint.furnitureSpec",
    defaultMessage: "Furniture spec",
  },
  "taskCreation.carpenter.extraDetails.hint.otherSpec": {
    id: "taskCreation.carpenter.extraDetails.hint.otherSpec",
    defaultMessage: "Other spec",
  },
  "taskCreation.carpenter.extraDetails.hint.stairsSpec": {
    id: "taskCreation.carpenter.extraDetails.hint.stairsSpec",
    defaultMessage: "Stairs spec",
  },
  // Electrician
  "taskCreation.electrician.extraDetails.hint.certificateOrIssue": {
    id: "taskCreation.electrician.extraDetails.hint.certificateOrIssue",
    defaultMessage: "Certificate or issue",
  },
  "taskCreation.electrician.extraDetails.hint.describeIssue": {
    id: "taskCreation.electrician.extraDetails.hint.describeIssue",
    defaultMessage: "Describe issue",
  },
  "taskCreation.electrician.extraDetails.hint.exactSymptom": {
    id: "taskCreation.electrician.extraDetails.hint.exactSymptom",
    defaultMessage: "Exact symptom",
  },
  "taskCreation.electrician.extraDetails.hint.knownConstraints": {
    id: "taskCreation.electrician.extraDetails.hint.knownConstraints",
    defaultMessage: "Known constraints",
  },
  "taskCreation.electrician.extraDetails.hint.lightFixtureLocation": {
    id: "taskCreation.electrician.extraDetails.hint.lightFixtureLocation",
    defaultMessage: "Light fixture location",
  },
  "taskCreation.electrician.extraDetails.hint.newBuildOrExisting": {
    id: "taskCreation.electrician.extraDetails.hint.newBuildOrExisting",
    defaultMessage: "New build or existing",
  },
  "taskCreation.electrician.extraDetails.hint.pointsCount": {
    id: "taskCreation.electrician.extraDetails.hint.pointsCount",
    defaultMessage: "Points count",
  },
  "taskCreation.electrician.extraDetails.hint.previousReport": {
    id: "taskCreation.electrician.extraDetails.hint.previousReport",
    defaultMessage: "Previous report",
  },
  "taskCreation.electrician.extraDetails.hint.scope": {
    id: "taskCreation.electrician.extraDetails.hint.scope",
    defaultMessage: "Scope",
  },
  "taskCreation.electrician.extraDetails.hint.sinceWhenAndScope": {
    id: "taskCreation.electrician.extraDetails.hint.sinceWhenAndScope",
    defaultMessage: "Since when and scope",
  },
  "taskCreation.electrician.extraDetails.hint.switchType": {
    id: "taskCreation.electrician.extraDetails.hint.switchType",
    defaultMessage: "Switch type",
  },
  // Handyman
  "taskCreation.handyman.extraDetails.hint.affectedPart": {
    id: "taskCreation.handyman.extraDetails.hint.affectedPart",
    defaultMessage: "Affected part",
  },
  "taskCreation.handyman.extraDetails.hint.assemblyStage": {
    id: "taskCreation.handyman.extraDetails.hint.assemblyStage",
    defaultMessage: "Assembly stage",
  },
  "taskCreation.handyman.extraDetails.hint.brandModel": {
    id: "taskCreation.handyman.extraDetails.hint.brandModel",
    defaultMessage: "Brand model",
  },
  "taskCreation.handyman.extraDetails.hint.fixingsSupplied": {
    id: "taskCreation.handyman.extraDetails.hint.fixingsSupplied",
    defaultMessage: "Fixings supplied",
  },
  "taskCreation.handyman.extraDetails.hint.hasEquipment": {
    id: "taskCreation.handyman.extraDetails.hint.hasEquipment",
    defaultMessage: "Has equipment",
  },
  "taskCreation.handyman.extraDetails.hint.hasScrewdriver": {
    id: "taskCreation.handyman.extraDetails.hint.hasScrewdriver",
    defaultMessage: "Has screwdriver",
  },
  "taskCreation.handyman.extraDetails.hint.installationHeight": {
    id: "taskCreation.handyman.extraDetails.hint.installationHeight",
    defaultMessage: "Installation height",
  },
  "taskCreation.handyman.extraDetails.hint.itemAndWall": {
    id: "taskCreation.handyman.extraDetails.hint.itemAndWall",
    defaultMessage: "Item and wall",
  },
  "taskCreation.handyman.extraDetails.hint.listJobs": {
    id: "taskCreation.handyman.extraDetails.hint.listJobs",
    defaultMessage: "List jobs",
  },
  "taskCreation.handyman.extraDetails.hint.replacementPartsBought": {
    id: "taskCreation.handyman.extraDetails.hint.replacementPartsBought",
    defaultMessage: "Replacement parts bought",
  },
  "taskCreation.handyman.extraDetails.hint.requiresWallMounting": {
    id: "taskCreation.handyman.extraDetails.hint.requiresWallMounting",
    defaultMessage: "Requires wall mounting",
  },
  // Lock repairer
  "taskCreation.lockRepairer.extraDetails.hint.doorOrWindowMaterial": {
    id: "taskCreation.lockRepairer.extraDetails.hint.doorOrWindowMaterial",
    defaultMessage: "Door or window material",
  },
  "taskCreation.lockRepairer.extraDetails.hint.doorTypeAndMaterial": {
    id: "taskCreation.lockRepairer.extraDetails.hint.doorTypeAndMaterial",
    defaultMessage: "Door type and material",
  },
  "taskCreation.lockRepairer.extraDetails.hint.doorTypeCylinder": {
    id: "taskCreation.lockRepairer.extraDetails.hint.doorTypeCylinder",
    defaultMessage: "Door type cylinder",
  },
  "taskCreation.lockRepairer.extraDetails.hint.handleProblem": {
    id: "taskCreation.lockRepairer.extraDetails.hint.handleProblem",
    defaultMessage: "Handle problem",
  },
  "taskCreation.lockRepairer.extraDetails.hint.keyState": {
    id: "taskCreation.lockRepairer.extraDetails.hint.keyState",
    defaultMessage: "Key state",
  },
  "taskCreation.lockRepairer.extraDetails.hint.motorisedOrManual": {
    id: "taskCreation.lockRepairer.extraDetails.hint.motorisedOrManual",
    defaultMessage: "Motorised or manual",
  },
  "taskCreation.lockRepairer.extraDetails.hint.newOrReplacement": {
    id: "taskCreation.lockRepairer.extraDetails.hint.newOrReplacement",
    defaultMessage: "New or replacement",
  },
  "taskCreation.lockRepairer.extraDetails.hint.urgency": {
    id: "taskCreation.lockRepairer.extraDetails.hint.urgency",
    defaultMessage: "Urgency",
  },
  "taskCreation.lockRepairer.extraDetails.hint.workAndType": {
    id: "taskCreation.lockRepairer.extraDetails.hint.workAndType",
    defaultMessage: "Work and type",
  },
  "taskCreation.lockRepairer.extraDetails.hint.workingKey": {
    id: "taskCreation.lockRepairer.extraDetails.hint.workingKey",
    defaultMessage: "Working key",
  },
  // Painter
  "taskCreation.painter.extraDetails.hint.claddingType": {
    id: "taskCreation.painter.extraDetails.hint.claddingType",
    defaultMessage: "Cladding type",
  },
  "taskCreation.painter.extraDetails.hint.materialsSuppliedByClient": {
    id: "taskCreation.painter.extraDetails.hint.materialsSuppliedByClient",
    defaultMessage: "Materials supplied by client",
  },
  "taskCreation.painter.extraDetails.hint.roomOccupied": {
    id: "taskCreation.painter.extraDetails.hint.roomOccupied",
    defaultMessage: "Room occupied",
  },
  "taskCreation.painter.extraDetails.hint.storeysCount": {
    id: "taskCreation.painter.extraDetails.hint.storeysCount",
    defaultMessage: "Storeys count",
  },
  "taskCreation.painter.extraDetails.hint.strippingNeeded": {
    id: "taskCreation.painter.extraDetails.hint.strippingNeeded",
    defaultMessage: "Stripping needed",
  },
  "taskCreation.painter.extraDetails.hint.surfaceCondition": {
    id: "taskCreation.painter.extraDetails.hint.surfaceCondition",
    defaultMessage: "Surface condition",
  },
  "taskCreation.painter.extraDetails.hint.surfaceConditionDetailed": {
    id: "taskCreation.painter.extraDetails.hint.surfaceConditionDetailed",
    defaultMessage: "Surface condition detailed",
  },
  // Plumber
  "taskCreation.plumber.extraDetails.hint.affectedAppliances": {
    id: "taskCreation.plumber.extraDetails.hint.affectedAppliances",
    defaultMessage: "Affected appliances",
  },
  "taskCreation.plumber.extraDetails.hint.cisternType": {
    id: "taskCreation.plumber.extraDetails.hint.cisternType",
    defaultMessage: "Cistern type",
  },
  "taskCreation.plumber.extraDetails.hint.flushProblem": {
    id: "taskCreation.plumber.extraDetails.hint.flushProblem",
    defaultMessage: "Flush problem",
  },
  "taskCreation.plumber.extraDetails.hint.leakConstantOrIntermittent": {
    id: "taskCreation.plumber.extraDetails.hint.leakConstantOrIntermittent",
    defaultMessage: "Leak constant or intermittent",
  },
  "taskCreation.plumber.extraDetails.hint.leakLocation": {
    id: "taskCreation.plumber.extraDetails.hint.leakLocation",
    defaultMessage: "Leak location",
  },
  "taskCreation.plumber.extraDetails.hint.productsTried": {
    id: "taskCreation.plumber.extraDetails.hint.productsTried",
    defaultMessage: "Products tried",
  },
  "taskCreation.plumber.extraDetails.hint.repairOrReplacement": {
    id: "taskCreation.plumber.extraDetails.hint.repairOrReplacement",
    defaultMessage: "Repair or replacement",
  },
  "taskCreation.plumber.extraDetails.hint.shutOffValveAccess": {
    id: "taskCreation.plumber.extraDetails.hint.shutOffValveAccess",
    defaultMessage: "Shut off valve access",
  },
  "taskCreation.plumber.extraDetails.hint.toiletMounting": {
    id: "taskCreation.plumber.extraDetails.hint.toiletMounting",
    defaultMessage: "Toilet mounting",
  },
  "taskCreation.plumber.extraDetails.hint.waterBackingUpElsewhere": {
    id: "taskCreation.plumber.extraDetails.hint.waterBackingUpElsewhere",
    defaultMessage: "Water backing up elsewhere",
  },

  /* Photos hints, conditional on the screen's own answers. */
  // Appliance repairer
  "taskCreation.applianceRepairer.photos.hint.applianceLabel": {
    id: "taskCreation.applianceRepairer.photos.hint.applianceLabel",
    defaultMessage: "Appliance label",
  },
  "taskCreation.applianceRepairer.photos.hint.brokenDevice": {
    id: "taskCreation.applianceRepairer.photos.hint.brokenDevice",
    defaultMessage: "Broken device",
  },
  // Carpenter
  "taskCreation.carpenter.photos.hint.currentState": {
    id: "taskCreation.carpenter.photos.hint.currentState",
    defaultMessage: "Current state",
  },
  "taskCreation.carpenter.photos.hint.sketchesIdeas": {
    id: "taskCreation.carpenter.photos.hint.sketchesIdeas",
    defaultMessage: "Sketches ideas",
  },
  // Electrician
  "taskCreation.electrician.photos.hint.affectedDevices": {
    id: "taskCreation.electrician.photos.hint.affectedDevices",
    defaultMessage: "Affected devices",
  },
  "taskCreation.electrician.photos.hint.fuseBox": {
    id: "taskCreation.electrician.photos.hint.fuseBox",
    defaultMessage: "Fuse box",
  },
  "taskCreation.electrician.photos.hint.lightingFittingsFans": {
    id: "taskCreation.electrician.photos.hint.lightingFittingsFans",
    defaultMessage: "Lighting fittings fans",
  },
  "taskCreation.electrician.photos.hint.nonConformityCertificate": {
    id: "taskCreation.electrician.photos.hint.nonConformityCertificate",
    defaultMessage: "Non conformity certificate",
  },
  "taskCreation.electrician.photos.hint.socketsSwitches": {
    id: "taskCreation.electrician.photos.hint.socketsSwitches",
    defaultMessage: "Sockets switches",
  },
  "taskCreation.electrician.photos.hint.wiringPowerNetworkDevice": {
    id: "taskCreation.electrician.photos.hint.wiringPowerNetworkDevice",
    defaultMessage: "Wiring power network device",
  },
  // Lock repairer
  "taskCreation.lockRepairer.photos.hint.cylinderFace": {
    id: "taskCreation.lockRepairer.photos.hint.cylinderFace",
    defaultMessage: "Cylinder face",
  },
  "taskCreation.lockRepairer.photos.hint.doorAndCurrentLock": {
    id: "taskCreation.lockRepairer.photos.hint.doorAndCurrentLock",
    defaultMessage: "Door and current lock",
  },
  "taskCreation.lockRepairer.photos.hint.lockFromOutside": {
    id: "taskCreation.lockRepairer.photos.hint.lockFromOutside",
    defaultMessage: "Lock from outside",
  },
  "taskCreation.lockRepairer.photos.hint.mechanism": {
    id: "taskCreation.lockRepairer.photos.hint.mechanism",
    defaultMessage: "Mechanism",
  },
  "taskCreation.lockRepairer.photos.hint.partConcerned": {
    id: "taskCreation.lockRepairer.photos.hint.partConcerned",
    defaultMessage: "Part concerned",
  },
  "taskCreation.lockRepairer.photos.hint.whatNeedsFixing": {
    id: "taskCreation.lockRepairer.photos.hint.whatNeedsFixing",
    defaultMessage: "What needs fixing",
  },
  // Painter
  "taskCreation.painter.photos.hint.doorsWindowsMetalToRepaint": {
    id: "taskCreation.painter.photos.hint.doorsWindowsMetalToRepaint",
    defaultMessage: "Doors windows metal to repaint",
  },
  "taskCreation.painter.photos.hint.eachRoomToRepaint": {
    id: "taskCreation.painter.photos.hint.eachRoomToRepaint",
    defaultMessage: "Each room to repaint",
  },
  "taskCreation.painter.photos.hint.exteriorToRepaint": {
    id: "taskCreation.painter.photos.hint.exteriorToRepaint",
    defaultMessage: "Exterior to repaint",
  },
  "taskCreation.painter.photos.hint.referenceImage": {
    id: "taskCreation.painter.photos.hint.referenceImage",
    defaultMessage: "Reference image",
  },
  "taskCreation.painter.photos.hint.surfaceToRepaint": {
    id: "taskCreation.painter.photos.hint.surfaceToRepaint",
    defaultMessage: "Surface to repaint",
  },
  // Plumber
  "taskCreation.plumber.photos.hint.affectedFixtures": {
    id: "taskCreation.plumber.photos.hint.affectedFixtures",
    defaultMessage: "Affected fixtures",
  },
  "taskCreation.plumber.photos.hint.affectedValve": {
    id: "taskCreation.plumber.photos.hint.affectedValve",
    defaultMessage: "Affected valve",
  },
  "taskCreation.plumber.photos.hint.tapSinkWashbasin": {
    id: "taskCreation.plumber.photos.hint.tapSinkWashbasin",
    defaultMessage: "Tap sink washbasin",
  },
  "taskCreation.plumber.photos.hint.toilet": {
    id: "taskCreation.plumber.photos.hint.toilet",
    defaultMessage: "Toilet",
  },

  /*
   * First `service_details` screen of each category (RT-4321 M5): the one
   * question's title and one label per option. The config carries neither —
   * options are bare strings — so both are resolved from the category key,
   * the question key and the option string by `getQuestionTitleMessage` /
   * `getQuestionOptionMessage` below. Painter carries the real Figma copy;
   * the rest are the usual derived placeholders (see this file's header).
   *
   * TODO(RT-4321): the later screens (`area`, `photos`, `extra_details`,
   * `time_and_location`) need their own titles and option labels when those
   * screens land.
   */
  // appliance_repairer
  "taskCreation.applianceRepairer.applianceType.title": {
    id: "taskCreation.applianceRepairer.applianceType.title",
    defaultMessage: "Appliance type",
  },
  "taskCreation.applianceRepairer.applianceType.option.washingMachineDryer": {
    id: "taskCreation.applianceRepairer.applianceType.option.washingMachineDryer",
    defaultMessage: "Washing machine dryer",
  },
  "taskCreation.applianceRepairer.applianceType.option.refrigeratorFreezer": {
    id: "taskCreation.applianceRepairer.applianceType.option.refrigeratorFreezer",
    defaultMessage: "Refrigerator freezer",
  },
  "taskCreation.applianceRepairer.applianceType.option.dishwasher": {
    id: "taskCreation.applianceRepairer.applianceType.option.dishwasher",
    defaultMessage: "Dishwasher",
  },
  "taskCreation.applianceRepairer.applianceType.option.ovenStovetopCookerMicrowave":
    {
      id: "taskCreation.applianceRepairer.applianceType.option.ovenStovetopCookerMicrowave",
      defaultMessage: "Oven stovetop cooker microwave",
    },
  "taskCreation.applianceRepairer.applianceType.option.rangeHoodVentilationAc":
    {
      id: "taskCreation.applianceRepairer.applianceType.option.rangeHoodVentilationAc",
      defaultMessage: "Range hood ventilation ac",
    },
  // bicycle_repairer
  "taskCreation.bicycleRepairer.taskType.title": {
    id: "taskCreation.bicycleRepairer.taskType.title",
    defaultMessage: "Task type",
  },
  "taskCreation.bicycleRepairer.taskType.option.punctureTireTube": {
    id: "taskCreation.bicycleRepairer.taskType.option.punctureTireTube",
    defaultMessage: "Puncture tire tube",
  },
  "taskCreation.bicycleRepairer.taskType.option.ebikeComponent": {
    id: "taskCreation.bicycleRepairer.taskType.option.ebikeComponent",
    defaultMessage: "Ebike component",
  },
  "taskCreation.bicycleRepairer.taskType.option.fullService": {
    id: "taskCreation.bicycleRepairer.taskType.option.fullService",
    defaultMessage: "Full service",
  },
  "taskCreation.bicycleRepairer.taskType.option.brakes": {
    id: "taskCreation.bicycleRepairer.taskType.option.brakes",
    defaultMessage: "Brakes",
  },
  "taskCreation.bicycleRepairer.taskType.option.gearsChainCables": {
    id: "taskCreation.bicycleRepairer.taskType.option.gearsChainCables",
    defaultMessage: "Gears chain cables",
  },
  "taskCreation.bicycleRepairer.taskType.option.wheelRimSpokes": {
    id: "taskCreation.bicycleRepairer.taskType.option.wheelRimSpokes",
    defaultMessage: "Wheel rim spokes",
  },
  // carpenter
  "taskCreation.carpenter.taskType.title": {
    id: "taskCreation.carpenter.taskType.title",
    defaultMessage: "Task type",
  },
  "taskCreation.carpenter.taskType.option.doorsWindowsFrames": {
    id: "taskCreation.carpenter.taskType.option.doorsWindowsFrames",
    defaultMessage: "Doors windows frames",
  },
  "taskCreation.carpenter.taskType.option.bespokeFurnitureCupboardsWardrobes": {
    id: "taskCreation.carpenter.taskType.option.bespokeFurnitureCupboardsWardrobes",
    defaultMessage: "Bespoke furniture cupboards wardrobes",
  },
  "taskCreation.carpenter.taskType.option.stairsBalustradesDecking": {
    id: "taskCreation.carpenter.taskType.option.stairsBalustradesDecking",
    defaultMessage: "Stairs balustrades decking",
  },
  "taskCreation.carpenter.taskType.option.parquetFlooringSkirting": {
    id: "taskCreation.carpenter.taskType.option.parquetFlooringSkirting",
    defaultMessage: "Parquet flooring skirting",
  },
  // electrician
  "taskCreation.electrician.taskType.title": {
    id: "taskCreation.electrician.taskType.title",
    defaultMessage: "Task type",
  },
  "taskCreation.electrician.taskType.option.lightingFittingsFans": {
    id: "taskCreation.electrician.taskType.option.lightingFittingsFans",
    defaultMessage: "Lighting fittings fans",
  },
  "taskCreation.electrician.taskType.option.socketsSwitches": {
    id: "taskCreation.electrician.taskType.option.socketsSwitches",
    defaultMessage: "Sockets switches",
  },
  "taskCreation.electrician.taskType.option.complianceFuseBoxDiagnostics": {
    id: "taskCreation.electrician.taskType.option.complianceFuseBoxDiagnostics",
    defaultMessage: "Compliance fuse box diagnostics",
  },
  "taskCreation.electrician.taskType.option.wiringPowerNetwork": {
    id: "taskCreation.electrician.taskType.option.wiringPowerNetwork",
    defaultMessage: "Wiring power network",
  },
  "taskCreation.electrician.taskType.option.faultsConnectionsTerminals": {
    id: "taskCreation.electrician.taskType.option.faultsConnectionsTerminals",
    defaultMessage: "Faults connections terminals",
  },
  // handyman
  "taskCreation.handyman.taskType.title": {
    id: "taskCreation.handyman.taskType.title",
    defaultMessage: "Task type",
  },
  "taskCreation.handyman.taskType.option.furnitureAssembly": {
    id: "taskCreation.handyman.taskType.option.furnitureAssembly",
    defaultMessage: "Furniture assembly",
  },
  "taskCreation.handyman.taskType.option.minorRepairs": {
    id: "taskCreation.handyman.taskType.option.minorRepairs",
    defaultMessage: "Minor repairs",
  },
  "taskCreation.handyman.taskType.option.wallMounting": {
    id: "taskCreation.handyman.taskType.option.wallMounting",
    defaultMessage: "Wall mounting",
  },
  "taskCreation.handyman.taskType.option.variousSmallJobs": {
    id: "taskCreation.handyman.taskType.option.variousSmallJobs",
    defaultMessage: "Various small jobs",
  },
  // lock_repairer
  "taskCreation.lockRepairer.taskType.title": {
    id: "taskCreation.lockRepairer.taskType.title",
    defaultMessage: "Task type",
  },
  "taskCreation.lockRepairer.taskType.option.lockedOut": {
    id: "taskCreation.lockRepairer.taskType.option.lockedOut",
    defaultMessage: "Locked out",
  },
  "taskCreation.lockRepairer.taskType.option.lockInstallationReplacement": {
    id: "taskCreation.lockRepairer.taskType.option.lockInstallationReplacement",
    defaultMessage: "Lock installation replacement",
  },
  "taskCreation.lockRepairer.taskType.option.cylinderChange": {
    id: "taskCreation.lockRepairer.taskType.option.cylinderChange",
    defaultMessage: "Cylinder change",
  },
  "taskCreation.lockRepairer.taskType.option.doorHandleHingeMechanism": {
    id: "taskCreation.lockRepairer.taskType.option.doorHandleHingeMechanism",
    defaultMessage: "Door handle hinge mechanism",
  },
  "taskCreation.lockRepairer.taskType.option.garageDoorGateShutter": {
    id: "taskCreation.lockRepairer.taskType.option.garageDoorGateShutter",
    defaultMessage: "Garage door gate shutter",
  },
  // painter
  "taskCreation.painter.surfaceType.title": {
    id: "taskCreation.painter.surfaceType.title",
    defaultMessage: "What type of surface?",
  },
  "taskCreation.painter.surfaceType.option.interiorWallsCeilings": {
    id: "taskCreation.painter.surfaceType.option.interiorWallsCeilings",
    defaultMessage: "Interior walls / ceilings",
  },
  "taskCreation.painter.surfaceType.option.doorsWindowsMetal": {
    id: "taskCreation.painter.surfaceType.option.doorsWindowsMetal",
    defaultMessage: "Doors / windows / metal",
  },
  "taskCreation.painter.surfaceType.option.facadeExterior": {
    id: "taskCreation.painter.surfaceType.option.facadeExterior",
    defaultMessage: "Facade / exterior",
  },
  /* painter, `area` screen — copy taken from the Figma design (1503-13070 /
     1503-15951), not from the placeholder convention. */
  "taskCreation.painter.area.screenTitle": {
    id: "taskCreation.painter.area.screenTitle",
    defaultMessage: "Details about the surface",
  },
  "taskCreation.painter.area.screenSubtitle": {
    id: "taskCreation.painter.area.screenSubtitle",
    defaultMessage:
      "If you don't have this information right now you can skip to the next step",
  },
  "taskCreation.painter.rooms.title": {
    id: "taskCreation.painter.rooms.title",
    defaultMessage: "Number of rooms",
  },
  "taskCreation.painter.floorAreaM2.title": {
    id: "taskCreation.painter.floorAreaM2.title",
    defaultMessage: "Approx. area (m²)",
  },
  // plumber
  "taskCreation.plumber.taskType.title": {
    id: "taskCreation.plumber.taskType.title",
    defaultMessage: "Task type",
  },
  "taskCreation.plumber.taskType.option.waterLeak": {
    id: "taskCreation.plumber.taskType.option.waterLeak",
    defaultMessage: "Water leak",
  },
  "taskCreation.plumber.taskType.option.tapSinkWashbasin": {
    id: "taskCreation.plumber.taskType.option.tapSinkWashbasin",
    defaultMessage: "Tap sink washbasin",
  },
  "taskCreation.plumber.taskType.option.unblockingDrainage": {
    id: "taskCreation.plumber.taskType.option.unblockingDrainage",
    defaultMessage: "Unblocking drainage",
  },
  "taskCreation.plumber.taskType.option.toiletFlush": {
    id: "taskCreation.plumber.taskType.option.toiletFlush",
    defaultMessage: "Toilet flush",
  },

  /* Category display names (RT-4321) — see the class comment above. */
  "taskCreation.category.diy": {
    id: "taskCreation.category.diy",
    defaultMessage: "DIY pros",
  },
  "taskCreation.category.painter": {
    id: "taskCreation.category.painter",
    defaultMessage: "painters",
  },
  "taskCreation.category.electrician": {
    id: "taskCreation.category.electrician",
    defaultMessage: "electricians",
  },
  "taskCreation.category.carpenter": {
    id: "taskCreation.category.carpenter",
    defaultMessage: "carpenters",
  },
  "taskCreation.category.appliance_repairer": {
    id: "taskCreation.category.appliance_repairer",
    defaultMessage: "appliance repairers",
  },
  "taskCreation.category.plumber": {
    id: "taskCreation.category.plumber",
    defaultMessage: "plumbers",
  },
  "taskCreation.category.handyman": {
    id: "taskCreation.category.handyman",
    defaultMessage: "handymen",
  },
  "taskCreation.category.lock_repairer": {
    id: "taskCreation.category.lock_repairer",
    defaultMessage: "lock repairers",
  },
  "taskCreation.category.bicycle_repairer": {
    id: "taskCreation.category.bicycle_repairer",
    defaultMessage: "bicycle repairers",
  },
});

export default configMessages;
